// Verbatim port of the v9.1 Dot Studio palette system (morfi-pattern-studio_9_1.html).
//
// These are the ONLY colors the generative DotEngine cover pipeline uses.
// No brand-blue override layer, no mosaic palette — colors come straight
// from the HTML's TOKENS → PALETTES → pickColor, baked into the final PNG.

import type { DerivedSeed } from './types';

/* ====================== CANONICAL TOKENS (mirror of CSS vars) ====================== */
const TOKENS = {
  brand: {
    50: '#EEF3FC',
    100: '#DAE7FF',
    200: '#B4CBF8',
    300: '#7CA3F3',
    400: '#346CF9',
    500: '#0000FF',
    600: '#0024B6',
    700: '#00198A',
    800: '#001263',
    900: '#010E42',
    950: '#000525',
  },
  gray: {
    50: '#FAFAFA',
    100: '#EFEFEF',
    200: '#E1E1E1',
    300: '#CACACA',
    400: '#A3A3A3',
    500: '#707070',
    600: '#464646',
    700: '#2B2B2B',
    800: '#1B1B1B',
    900: '#121212',
    950: '#080808',
  },
} as const;

const B: typeof TOKENS.brand = TOKENS.brand;
const G: typeof TOKENS.gray = TOKENS.gray;

export interface DotPalette {
  readonly label: string;
  readonly background: string;
  readonly scale: 'brand' | 'gray';
  /** [dominant, accent, bright, deep] */
  readonly colors: readonly [string, string, string, string];
  readonly labels: readonly [string, string, string, string];
}

export type DotPaletteName = 'brand' | 'mono' | 'inkPaper' | 'brandLite';

/* Palettes are built ONLY from tokens, so everything stays on-system.
   colors = [dominant, accent, bright, deep]; labels match for the pickers. */
export const DOT_PALETTES: Record<DotPaletteName, DotPalette> = {
  brand: {
    label: 'Brand',
    background: B[500],
    scale: 'brand',
    colors: [B[200], B[100], B[50], B[400]],
    labels: ['200', '100', '50', '400'],
  },
  mono: {
    label: 'Mono',
    background: G[950],
    scale: 'gray',
    colors: [G[300], G[200], G[100], G[500]],
    labels: ['300', '200', '100', '500'],
  },
  inkPaper: {
    label: 'Paper',
    background: G[50],
    scale: 'gray',
    colors: [G[700], G[900], G[600], G[800]],
    labels: ['700', '900', '600', '800'],
  },
  brandLite: {
    label: 'Tint',
    background: B[50],
    scale: 'brand',
    colors: [B[500], B[700], B[400], B[800]],
    labels: ['500', '700', '400', '800'],
  },
};

export const DOT_PALETTE_NAMES: readonly DotPaletteName[] = Object.keys(
  DOT_PALETTES,
) as readonly DotPaletteName[];

// Palettes where the background is the DARKEST value → dot tones must be lighter (lower step).
// The rest (Paper, Tint) are light-mode → dot tones must be darker (higher step).
export const BG_DARKEST: readonly string[] = ['brand', 'mono', 'mix'];

export const ASPECTS = {
  square: { w: 1080, h: 1080 },
  portrait: { w: 1080, h: 1350 },
  story: { w: 1080, h: 1920 },
  landscape: { w: 1920, h: 1080 },
  banner: { w: 1680, h: 720 },
  cover: { w: 1200, h: 400 },
} as const;

export type AspectName = keyof typeof ASPECTS;

export function isDotPaletteName(name: string): name is DotPaletteName {
  return name in DOT_PALETTES;
}

export function dotPaletteColors(name: DotPaletteName): readonly [string, string, string, string] {
  return DOT_PALETTES[name].colors;
}

/* ---- color picking (palette-aware) — verbatim from DotEngine ---- */
export function pickColor(
  colors: readonly [string, string, string, string],
  derived: Pick<DerivedSeed, 'skew'> | null,
  shift: number,
): string {
  const [c0, c1, c2, c3] = colors;
  const skew = derived ? derived.skew : 0.5;
  const tilt = (skew - 0.5) * 0.4;
  if (shift > 0.93 - tilt) return c2;
  if (shift > 0.72 - tilt) return c1;
  if (shift < 0.1 + tilt) return c3;
  return c0;
}
